import protobuf from 'protobufjs';
import { MARKET_1X2, MARKET_1X2_H1, MARKET_NEXTGOAL, MARKET_OU, MARKET_OU_H1 } from "./markets";

export const get_proto_root = (() => {
    let root;
    
    return async () => { 
        if (!root) 
        {
            console.log('loading root for the first time');
            const f_names = ['event', 'game', 'market', 'odd', 'scores', 'team']
                .map(x => process.env.PUBLIC_URL + '/protos/' +  x + '.proto');

            root = protobuf.load(f_names);
        }

        return root;
    }
})();

/**
 * converts a base64 encoded string into a uint8 array
 * @param {String} base64 
 * @returns {Uint8Array}
 */
export function toUint8Array(base64) {
    // Decode the base64 string to a binary string
    const binaryString = atob(base64);
    
    // Create a Uint8Array with the same length as the binary string
    const uint8Array = new Uint8Array(binaryString.length);
    
    // Convert each character in the binary string to a byte in the Uint8Array
    for (let i = 0; i < binaryString.length; i++) {
        uint8Array[i] = binaryString.charCodeAt(i);
    }

    return uint8Array;
}

const DEFAULT_SCORES_PROP_VALUE = [0, 0];

export const get_half_time_goals = (scores) => {
	return scores?.['H1'] ?? DEFAULT_SCORES_PROP_VALUE;
}

export const get_full_time_goals = (scores) => {
	return scores?.['GOAL'] ?? DEFAULT_SCORES_PROP_VALUE;
}
export const  toPair=(x) =>{
	if (Array.isArray(x) && x.length >= 2) {
	  return [
		Number.isFinite(Number(x[0])) ? Number(x[0]) : 0,
		Number.isFinite(Number(x[1])) ? Number(x[1]) : 0,
	  ];
	}
	return [0, 0];
  }

export const get_page_events_ = (events_arr, page = 1, size = 10) => {
	//const sorted = events_arr.sort((a , b) => b.obj[0].tm - a.obj[0].tm);
  const sorted = [...events_arr].sort((a, b) =>
    (Number(b?.obj?.[0]?.seconds) || 0) - (Number(a?.obj?.[0]?.seconds) || 0)
  );

	const start = --page * size;
	const end = start + size;
	const page_events = sorted.slice(start, end);

	return page_events;
};
const toSeconds = (x) => {
  if (typeof x === "number") return x;
  if (typeof x === "string" && x.includes(":")) {
    const [m, s] = x.split(":");
    const mm = Number(m), ss = Number(s);
    if (Number.isFinite(mm) && Number.isFinite(ss)) return mm * 60 + ss;
  }
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

export const get_page_events = (events_arr, page = 1, size = 10) => {
  const sorted = [...events_arr].sort(
    (a, b) => toSeconds(b?.obj?.[0]?.seconds) - toSeconds(a?.obj?.[0]?.seconds)
  );

  const start = (page - 1) * size;
  return sorted.slice(start, start + size);
};
export const get_red_cards = scores => {
	return scores?.['RED_CARD'] ?? DEFAULT_SCORES_PROP_VALUE;
};
// ---------- name utils ----------

// ---------- name + period utils ----------

const norm = (s) => String(s ?? '').toUpperCase().replace(/\s+/g, '');
const nameIsOneOf = (name, list) => {
  const n = norm(name);
  return list.some((x) => n === norm(x));
};

// Detect period of a market based on its `n` (name/code)
const inferPeriodTag = (name) => {
  const n = String(name ?? '').toUpperCase();

  // explicit codes from our backend mapping
  if (n.includes('_H1') || /\b1STHALF\b/.test(n) || /\bH1\b/.test(n)) return 'H1';
  if (n.includes('_H2') || /\b2NDHALF\b/.test(n) || /\bH2\b/.test(n)) return 'H2';

  // generic textual half markers (just in case)
  if (/\(1STHALF\)/.test(n) || n.includes('1ST HALF')) return 'H1';
  if (/\(2NDHALF\)/.test(n) || n.includes('2ND HALF')) return 'H2';

  return 'FT';
};

// Partition markets into FT / 1st half / 2nd half buckets
const partitionByPeriod = (odds) => {
  const res = { FT: [], H1: [], H2: [] };
  (Array.isArray(odds) ? odds : []).forEach((m) => {
    const tag = inferPeriodTag(m?.n);
    res[tag].push(m);
  });
  return res;
};

// Find a market by any of the provided names/aliases, optionally restricted to a period tag
const findMarket = (odds, aliases, periodTag) => {
  const arr = Array.isArray(odds) ? odds : [];
  return arr.find((m) => {
    if (periodTag && inferPeriodTag(m?.n) !== periodTag) return false;
    return nameIsOneOf(m?.n, aliases);
  });
};

// Pull an option by name aliases
const findOpt = (opts, aliases) => {
  const arr = Array.isArray(opts) ? opts : [];
  return arr.find((o) => nameIsOneOf(o?.n, aliases));
};

/* =========================
   MATCH RESULT (1X2)
   ========================= */

// Full-time 1X2 -> returns array in order [1, X, 2]
export const getFullTimeOddsMatchResult = (odds) => {
  const { FT } = partitionByPeriod(odds);

  // Accept common aliases
  const mr = findMarket(
    FT,
    ['1X2', '1x2', 'MATCH ODDS', 'MATCH RESULT', 'FULL TIME RESULT', 'FULLTIME RESULT', 'WIN-DRAW-WIN'],
    'FT'
  );

  const opts = Array.isArray(mr?.o) ? mr.o : [];
  const o1 = findOpt(opts, ['1', 'HOME', 'H']);
  const oX = findOpt(opts, ['X', 'DRAW', 'D']);
  const o2 = findOpt(opts, ['2', 'AWAY', 'A']);

  const out = [];
  if (o1) out.push({ ...o1, Odd: o1.v });
  if (oX) out.push({ ...oX, Odd: oX.v });
  if (o2) out.push({ ...o2, Odd: o2.v });
  return out;
};

// Half-time 1X2 (HT result)
export const getHalfTimeOddsMatchResult = (odds) => {
  const { H1 } = partitionByPeriod(odds);

  // Only look in H1 markets
  const mrH1 = findMarket(
    H1,
    ['1X2_H1', '1x2_H1', '1X2 (1ST HALF)', 'HALF TIME RESULT', 'HALFTIMERESULT'],
    'H1'
  );

  const opts = Array.isArray(mrH1?.o) ? mrH1.o : [];
  const o1 = findOpt(opts, ['1', 'HOME', 'H']);
  const oX = findOpt(opts, ['X', 'DRAW', 'D']);
  const o2 = findOpt(opts, ['2', 'AWAY', 'A']);

  const out = [];
  if (o1) out.push({ ...o1, Odd: o1.v });
  if (oX) out.push({ ...oX, Odd: oX.v });
  if (o2) out.push({ ...o2, Odd: o2.v });
  return out;
};

/* =========================
   OVER / UNDER (OU)
   ========================= */

// Full-time OU (line optional). Returns { line, wireOdds:[Under, Over] }
export const getFullTimeOddsUnderOver = (odds) => {
  const { FT } = partitionByPeriod(odds);

  const ou = findMarket(
    FT,
    ['OU', 'OVER/UNDER', 'UNDER/OVER', 'TOTALS', 'TOTAL GOALS', 'MATCH GOALS', 'OVER/UNDER LINE'],
    'FT'
  );

  const opts = Array.isArray(ou?.o) ? ou.o : [];

  const rawLine = ou && (ou.l ?? ou.h);
  const line = Number.isFinite(Number(rawLine)) ? Number(rawLine) : 0;

  const under = findOpt(opts, ['UNDER', 'U']);
  const over  = findOpt(opts, ['OVER', 'O']);

  const wireOdds = [];
  if (under) wireOdds.push({ ...under, Odd: under.v });
  if (over)  wireOdds.push({ ...over,  Odd: over.v  });

  return { line, wireOdds };
};

// Half-time OU (line optional). Returns { line, wireOdds:[Under, Over] }
export const getHalfTimeOddsUnderOver = (odds) => {
  const { H1 } = partitionByPeriod(odds);

  // Support either explicit suffix or a market named for half
  const ouH1 = findMarket(
    H1,
    ['OU_H1', 'OVER/UNDER (1ST HALF)', 'OVER/UNDER 1ST HALF', 'OU 1ST HALF', '1ST HALF OU'],
    'H1'
  );

  const opts = Array.isArray(ouH1?.o) ? ouH1.o : [];

  const rawLine = ouH1 && (ouH1.l ?? ouH1.h);
  const line = Number.isFinite(Number(rawLine)) ? Number(rawLine) : 0;

  const under = findOpt(opts, ['UNDER', 'U']);
  const over  = findOpt(opts, ['OVER', 'O']);

  const wireOdds = [];
  if (under) wireOdds.push({ ...under, Odd: under.v });
  if (over)  wireOdds.push({ ...over,  Odd: over.v  });

  return { line, wireOdds };
};

/* =========================
   BOTH TEAMS TO SCORE (BTS)
   ========================= */

export const getGoalGoalOdds = (odds, market) => {
  // Accept aliases for BTS/GG; `market` can be 'BTS', 'BTS_H1', 'BTS_H2', etc.
  const mrk = findMarket(
    odds, // BTS can be FT or HT depending on code passed in
    [market, 'BTS', 'BTTS', 'GG', 'BOTH TEAMS TO SCORE']
  );

  const opts = Array.isArray(mrk?.o) ? mrk.o : [];
  const yes = findOpt(opts, ['YES', 'Y']);
  const no  = findOpt(opts, ['NO', 'N']);

  const out = [];
  if (yes) out.push({ ...yes, Odd: yes.v });
  if (no)  out.push({ ...no,  Odd: no.v  });
  return out;
};

/* =========================
   NEXT GOAL (S{n}G)
   ========================= */

export const getNextGoalOdds = (odds, totalGoals) => {
  // Markets are named S1G, S2G, ... for next goal
  const nextName = `S${(Number(totalGoals) || 0) + 1}G`;
  const ng = findMarket(odds, [nextName, 'NEXT GOAL', 'NEXT_GOAL']);
  const opts = Array.isArray(ng?.o) ? ng.o : [];
  return opts.map((x) => ({ ...x, Odd: x.v }));
};

export const getNextGoalOptionOdd = (option, odds) => {
  const arr = Array.isArray(odds) ? odds : [];
  const hit =
    arr.find((o) => nameIsOneOf(o?.n, [option])) ||
    (norm(option) === 'NOGOAL'
      ? arr.find((o) => nameIsOneOf(o?.n, ['NO GOAL', 'NO_GOAL', 'NG']))
      : undefined);
  return hit || { n: option, v: undefined };
};

/* =========================
   Proto -> BT mapper (unchanged)
   ========================= */

// Bet365-style -> GS-style `bt` mapper
export function mapProtoOddsToBT(odds = []) {
  // odds: [{ id, name, participants: [{ name, value, handicap, suspend }] }]
  return odds.map((market) => ({
    n: market.name, // BetCategory / code
    h: undefined,   // (optional) line at market level
    o: (market.participants || []).map((p) => ({
      n: p.name,                            // BetOption
      v: Number(p.value),                   // price
      h: p.handicap != null ? String(p.handicap) : undefined, // Line (as string)
      s: p.suspend ? Number(p.suspend) : 0, // suspend flag if present
    })),
  }));
}


export const  toUint8ArrayFromBase64=(base64)=> {
    // Browser-safe
    const bin = atob(base64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}
export const coerceToUint8Array=(message)=> {
    // Accept base64 string, ArrayBuffer, Uint8Array, or Buffer
    if (typeof message === 'string') {
        // assume base64
        return toUint8ArrayFromBase64(message);
    }
    if (message instanceof ArrayBuffer) {
        return new Uint8Array(message);
    }
    if (message?.buffer instanceof ArrayBuffer) {
        return new Uint8Array(message.buffer, message.byteOffset || 0, message.byteLength);
    }
    // Socket.IO can also send as { data: "<base64>" }
    if (message && typeof message.data === 'string') {
        return toUint8ArrayFromBase64(message.data);
    }
    console.warn('[buffers] Unknown message payload type:', message);
    return null;
}
// Bet365-style -> GS-style `bt` mapper
